import { useState } from 'react';
import { Link as RouterLink } from '@tanstack/react-router';
import type { OrgRole, Project } from '@ddp/shared';
import Button from '@carbon/react/es/components/Button';
import Modal from '@carbon/react/es/components/Modal/Modal';
import TextInput from '@carbon/react/es/components/TextInput/TextInput';
import TextArea from '@carbon/react/es/components/TextArea/TextArea';
import { InlineNotification } from '@carbon/react/es/components/Notification/Notification';
import Add from '@carbon/icons-react/es/Add';

import { useAuthStore } from '../../auth/store.js';
import { useT } from '../../i18n/index.js';
import { HelpHint } from '../../components/HelpHint.js';
import { useProjects } from './use-projects.js';

/** L'écriture (créer/renommer/supprimer) exige EDITOR+ ; la lecture est ouverte à tous les rôles. */
const ROLE_RANK: Record<OrgRole, number> = {
  VIEWER: 0,
  ANNOTATOR: 1,
  EDITOR: 2,
  ADMIN: 3,
  OWNER: 4,
};

type FormState = { id?: string; name: string; description: string };

/**
 * Section « Projets » (ADR-0024, incrément 2b) : liste des projets transverses avec leur nombre de
 * vidéos, et leur gestion (créer, renommer, supprimer) pour un rôle éditeur.
 *
 * `readOnly` : sur l'**Overview**, on ne fait que **lister** et on renvoie vers la page **Data**, où
 * s'opère la création/gestion. Ailleurs (Data), le panneau est pleinement éditable.
 */
export function ProjectsPanel({
  readOnly = false,
  anchorId = 'overview-projects',
}: {
  readOnly?: boolean;
  anchorId?: string;
} = {}) {
  const t = useT();
  const { projects, create, rename, remove } = useProjects();
  const role = useAuthStore((state) => state.currentUser?.memberships[0]?.role ?? null);
  const canManage = !readOnly && role !== null && ROLE_RANK[role] >= ROLE_RANK.EDITOR;

  const [form, setForm] = useState<FormState | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Project | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submitForm = async () => {
    if (!form || form.name.trim().length < 2) return;
    setBusy(true);
    setError(null);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
      };
      if (form.id) await rename(form.id, payload);
      else await create(payload);
      setForm(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    setError(null);
    try {
      await remove(deleteTarget.id);
      setDeleteTarget(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id={anchorId} className="ddp-projects" aria-label={t('overview.projects.title')}>
      <div className="ddp-overview__section-head">
        <h2 className="ddp-overview__subtitle">
          {t('overview.projects.title')}{' '}
          <span className="ddp-projects__count">{projects.length}</span>
          <HelpHint textKey="help.overview.projects" />
        </h2>
        {readOnly ? (
          // Overview : lecture seule, on renvoie vers la page dédiée pour créer/gérer.
          <RouterLink to="/data" hash="data-projects" className="ddp-overview__seeall">
            {t('overview.projects.manage')}
          </RouterLink>
        ) : (
          canManage && (
            <Button
              size="sm"
              kind="ghost"
              renderIcon={Add}
              onClick={() => {
                setError(null);
                setForm({ name: '', description: '' });
              }}
            >
              {t('overview.projects.new')}
            </Button>
          )
        )}
      </div>

      {projects.length === 0 ? (
        canManage ? (
          // Carte de création (comme la tuile « Autres images ») : le vide devient une action, pas
          // seulement un message. Le bouton « Nouveau projet » en tête reste le second chemin.
          <ul className="ddp-projects__grid">
            <li>
              <button
                type="button"
                className="ddp-more-card"
                onClick={() => {
                  setError(null);
                  setForm({ name: '', description: '' });
                }}
              >
                <span className="ddp-more-card__plus" aria-hidden="true">
                  0
                </span>
                <span className="ddp-more-card__label">{t('overview.projects.title')}</span>
                <span className="ddp-more-card__hint">{t('overview.projects.new')}</span>
              </button>
            </li>
          </ul>
        ) : (
          <p className="ddp-overview__empty">{t('overview.projects.empty')}</p>
        )
      ) : (
        <ul className="ddp-projects__grid">
          {projects.map((project) => (
            <li key={project.id} className="ddp-projects__card">
              <RouterLink
                to="/projects/$projectId"
                params={{ projectId: project.id }}
                className="ddp-projects__name"
              >
                {project.name}
              </RouterLink>
              {project.description && (
                <span className="ddp-projects__desc">{project.description}</span>
              )}
              <RouterLink
                to="/projects/$projectId"
                params={{ projectId: project.id }}
                className="ddp-projects__videos"
              >
                {project.videoCount} {t('overview.projects.videos')}
              </RouterLink>
              {canManage && (
                <div className="ddp-projects__actions">
                  <button
                    type="button"
                    onClick={() => {
                      setError(null);
                      setForm({
                        id: project.id,
                        name: project.name,
                        description: project.description ?? '',
                      });
                    }}
                  >
                    {t('overview.projects.rename')}
                  </button>
                  <button type="button" onClick={() => setDeleteTarget(project)}>
                    {t('overview.projects.delete')}
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {form && (
        <Modal
          open
          modalHeading={form.id ? t('overview.projects.rename') : t('overview.projects.new')}
          primaryButtonText={t('overview.projects.save')}
          secondaryButtonText={t('overview.projects.cancel')}
          primaryButtonDisabled={busy || form.name.trim().length < 2}
          onRequestClose={() => setForm(null)}
          onRequestSubmit={() => void submitForm()}
        >
          {error && (
            <InlineNotification
              kind="error"
              title={t('common.error')}
              subtitle={error}
              lowContrast
            />
          )}
          <TextInput
            id="project-name"
            labelText={t('overview.projects.formName')}
            placeholder={t('overview.projects.namePlaceholder')}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <TextArea
            id="project-desc"
            labelText={t('overview.projects.formDesc')}
            rows={2}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </Modal>
      )}

      {deleteTarget && (
        <Modal
          open
          danger
          modalHeading={t('overview.projects.deleteTitle')}
          primaryButtonText={t('overview.projects.delete')}
          secondaryButtonText={t('overview.projects.cancel')}
          onRequestClose={() => setDeleteTarget(null)}
          onRequestSubmit={() => void confirmDelete()}
        >
          {error && (
            <InlineNotification
              kind="error"
              title={t('common.error')}
              subtitle={error}
              lowContrast
            />
          )}
          <p>
            <strong>{deleteTarget.name}</strong>
          </p>
          <p>{t('overview.projects.deleteBody')}</p>
        </Modal>
      )}
    </section>
  );
}
